import { Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { BaseResolver } from '@app/app.resolver';
import { BenefitInvoice } from '@app/models/benefits/benefit-invoice.model';
import { ModelFactory } from '@app/models/core/model-factory';
import { AuthService } from '@app/services';
import { BenefitChangeRequest } from '@models/benefits/benefit-change-request.model';
import { BenefitPlan } from '@models/benefits/benefit-plan.model';
import { Benefit } from '@models/benefits/benefit.model';
import { BenefitShowMenuService } from './services';

@Injectable()
export class BenefitsResolver extends BaseResolver {
    protected modelFactory: ModelFactory = new ModelFactory([
        {
            className: 'benefitPlan',
            production: {
                find: (id: number) => {
                    return BenefitPlan.param('company', this.auth.company.id)
                        .with([
                            'employees',
                            'benefitProvider',
                            'benefitPolicy',
                            'benefitPolicy.divisions',
                            'file',
                            'benefits',
                            'benefits.benefitProvider',
                            'benefits.file',
                            'benefits.benefitCategory',
                            'benefits.benefitDetails',
                            'benefits.benefitDetails.benefitType',
                        ])
                        .find(id)
                        .then((benefitPlan: BenefitPlan) => {
                            let menuService: BenefitShowMenuService | undefined;

                            // Provide the benefitPlan to the menu service for filtering
                            try {
                                menuService = this.injector.get(BenefitShowMenuService);
                                if (!menuService) {
                                    throw new Error('BenefitShowMenuService not available');
                                }
                            } catch (e) {
                                // If for any reason the menuService isn't available, we just continue with the model resolution
                                return benefitPlan;
                            }

                            menuService.hasEnhancedManagedBenefits = !!benefitPlan.hasEnhancedManagedBenefits; // Change requests tab only appears when hasEnhancedManagedBenefits is true

                            return benefitPlan;
                        });
                },
            },
        },
        {
            className: 'benefit',
            production: {
                find: (id: number, route: ActivatedRouteSnapshot) => {
                    return Benefit.param('benefitPlan', route.params.benefitPlan)
                        .param('company', this.auth.company.id)
                        .with([
                            'benefitProvider',
                            'file',
                            'benefitCategory',
                            'benefitDetails',
                            'benefitDetails.benefitType',
                        ])
                        .find(id);
                },
            },
        },
        {
            className: 'benefitChangeRequest',
            production: {
                find: (id: number, route: ActivatedRouteSnapshot): Promise<BenefitChangeRequest> => {
                    return BenefitChangeRequest.param('benefitPlan', route.params.benefitPlan)
                        .param('company', this.auth.company.id)
                        .with(['employee', 'admin', 'employeeDocument'])
                        .find(id);
                },
            },
        },
        {
            className: 'benefitInvoice',
            production: {
                find: (id: number, route: ActivatedRouteSnapshot): Promise<BenefitInvoice> => {
                    return BenefitInvoice.with(['benefitProvider']).find(id);
                },
            },
        },
    ]);

    constructor(
        protected auth: AuthService,
        protected router: Router,
        private injector: Injector
    ) {
        super(auth, router);
    }
}
